import { io, Socket } from 'socket.io-client'

import { defineStore } from 'pinia'

import { useAuthStore } from './AuthStore'
import { useCallCenterStore } from './CallCenterStore'
import userStatuses from '@/utilities/userStatuses.json'

// Types
interface SocketUser {
  id: string
  type: string
  name: string
  activeCallCenter: number | null
  agentStatus: string
  presence: string
  status?: string
  presenceDisplay: {
    text: string
    icon: string
    color: string
    presence: string
    status: string | null
    uid: string
  }
}

interface Conference {
  name: string
  direction: 'inbound' | 'outbound'
  participants: unknown[]
  status: string
  connectedAt?: string
  endedAt?: string
  endedReason?: string
  wrappedUpAt?: string
  wrapUpData?: unknown
  agentId?: string
}

interface SocketData {
  id: string | null
  type: string | null
  name: string | null
  activeCallCenter: number | null
  agentStatus: string
}

/**
 * Gets data to send to socket
 * @param authStore - The auth store instance
 * @param callCenterStore - The call center store instance
 * @param agentStatus - Current agent status
 * @returns Socket data object
 */
const getDataToSendToSocket = (
  authStore: ReturnType<typeof useAuthStore>,
  callCenterStore: ReturnType<typeof useCallCenterStore>,
  agentStatus: string
): SocketData => {
  return {
    id: authStore.details.id,
    type: authStore.details.userType,
    name: authStore.details.name,
    activeCallCenter: parseInt(callCenterStore.selectedCallCenter || '0') || null,
    agentStatus: agentStatus
  }
}

/**
 * Computes presenceDisplay for a user based on their presence status
 * @param user - User object to compute presenceDisplay for
 * @returns User with computed presenceDisplay
 */
const computePresenceDisplay = (user: SocketUser): SocketUser => {
  // Socket sends presence as 'status', fall back to 'presence' if status is not available
  const presenceValue = user.status || user.presence
  const statusData = userStatuses[presenceValue as keyof typeof userStatuses]

  let presenceDisplay: SocketUser['presenceDisplay']

  if (statusData) {
    presenceDisplay = {
      ...statusData,
      presence: presenceValue,
      status: user.agentStatus || null,
      uid: user.id
    } as SocketUser['presenceDisplay']
  } else {
    // Fallback to offline if presence is not recognized
    presenceDisplay = {
      ...userStatuses.offline,
      presence: 'offline',
      status: null,
      uid: user.id
    } as SocketUser['presenceDisplay']
  }

  return {
    ...user,
    presence: presenceValue, // Ensure presence is set to the computed value
    presenceDisplay
  }
}

export const useSocketStore = defineStore('socketStore', {
  state: (): {
    socket: Socket | null
    socketIsConnected: boolean
    activeUsers: SocketUser[]
    usersPresence: SocketUser[]
    callCenterConferences: Conference[]
    agentStatus: string
  } => ({
    socket: null,
    socketIsConnected: false,
    activeUsers: [],
    usersPresence: [],
    callCenterConferences: [],
    agentStatus: 'offline'
  }),

  actions: {
    /**
     * Initializes socket connection
     */
    initializeSocket(): void {
      const authStore = useAuthStore()
      const callCenterStore = useCallCenterStore()

      if (authStore.details.id === null) return

      // Disconnect existing socket if it exists
      if (this.socket) {
        this.socket.disconnect()
        this.socket = null
        this.socketIsConnected = false
      }

      // Initialize socket.io
      this.socket = io(import.meta.env.VITE_SOCKET_IO_SERVER_URL, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 50,
        query: {
          user: JSON.stringify(getDataToSendToSocket(authStore, callCenterStore, this.agentStatus))
        }
      })

      this.socket.on('connect', () => {
        console.info(`Socket IO Connected! SocketID: ${this.socket?.id || 'unknown'}`)
        this.socketIsConnected = true
      })

      this.socket.on('disconnect', () => {
        console.info('Socket IO Disconnected')

        // Set agent status to offline
        this.agentStatus = 'offline'
        this.socketIsConnected = false
      })

      this.socket.on('users-all', (users: SocketUser[]) => {
        this.activeUsers = users.map(user => computePresenceDisplay(user))
      })

      this.socket.on('user-updated', (user: SocketUser) => {
        const updatedUser = computePresenceDisplay(user)
        this.activeUsers = this.activeUsers.map(u => (u.id === updatedUser.id ? updatedUser : u))

        // if the user is the current user, update the user details
        if (authStore.details.id === updatedUser.id) {
          this.agentStatus = updatedUser.agentStatus
          callCenterStore.selectedCallCenter = updatedUser.activeCallCenter
            ? updatedUser.activeCallCenter.toString()
            : null
        }
      })

      this.socket.on('user-added', (user: SocketUser) => {
        this.activeUsers.push(computePresenceDisplay(user))
      })

      this.socket.on('user-removed', (user: SocketUser) => {
        this.activeUsers = this.activeUsers.filter(u => u.id !== user.id)
      })

      this.socket.on('call-center-conferences', (conferences: Conference[]) => {
        this.callCenterConferences = conferences
      })

      this.socket.on('conference-added', (conference: Conference) => {
        // Check if the conference is already in the array
        if (this.callCenterConferences.some(c => c.name === conference.name)) return

        this.callCenterConferences.push(conference)
      })

      this.socket.on('conference-updated', (conference: Conference) => {
        // Find the conference in the array
        const index = this.callCenterConferences.findIndex(c => c.name === conference.name)

        // If not found, return
        if (index === -1) return

        // Update the call info without mutating the wrapUpData
        const updatedConference = this.callCenterConferences[index]
        updatedConference.participants = conference.participants
        updatedConference.status = conference.status
        updatedConference.connectedAt = conference.connectedAt
        updatedConference.endedAt = conference.endedAt
        updatedConference.endedReason = conference.endedReason
        updatedConference.wrappedUpAt = conference.wrappedUpAt
        updatedConference.wrapUpData = conference.wrapUpData
        updatedConference.agentId = conference.agentId

        // No need to replace the entire array
        this.callCenterConferences.splice(index, 1, updatedConference)
      })

      this.socket.on('conference-removed', (conference: Conference) => {
        // Get the index of the conference in the array
        const index = this.callCenterConferences.findIndex(c => c.name === conference.name)

        // If not found, return
        if (index === -1) return

        // Remove the conference from the array
        this.callCenterConferences.splice(index, 1)
      })
    },

    /**
     * Disconnects socket connection
     */
    disconnectSocket(): void {
      if (this.socket) {
        this.socket.disconnect()
        this.socket = null
        this.socketIsConnected = false
      }
    },

    /**
     * Gets user presence display information
     * @param userId - User ID to get presence for
     * @returns User presence display object
     */
    getUserPresence(userId: string): SocketUser['presenceDisplay'] {
      const user = this.activeUsers.find(u => u.id === userId) as SocketUser

      if (!user) {
        return {
          text: 'Offline',
          icon: 'xmark',
          color: 'bg-quaternary fg-quaternary',
          presence: 'offline',
          status: null,
          uid: userId
        }
      }

      // Ensure presenceDisplay is computed (defensive check)
      if (!user.presenceDisplay || user.presenceDisplay.uid !== userId) {
        const updatedUser = computePresenceDisplay({ ...user })
        return updatedUser.presenceDisplay
      }

      return user.presenceDisplay
    },

    /**
     * Updates agent status
     * @param status - New agent status
     * @param userId - User ID to update status for
     */
    updateAgentStatus(status: string, userId?: string): void {
      if (!this.socket) {
        return
      }

      const authStore = useAuthStore()
      const callCenterStore = useCallCenterStore()

      if (userId) {
        this.socket.emit('user-update-agent-status', { id: userId, agentStatus: status })
      } else {
        this.agentStatus = status
        this.socket.emit('user-update-agent-status', getDataToSendToSocket(authStore, callCenterStore, status))
      }
    },

    /**
     * Sets user presence
     * @param presence - Presence status
     * @param userId - User ID to set presence for
     */
    setUserPresence(presence: string, userId: string): void {
      if (!this.socket) {
        return
      }

      this.socket.emit('set-user-presence', { id: userId, presence: presence })
    },

    /**
     * Triggers user activity
     */
    triggerUserActivity(): void {
      if (this.socket) {
        const authStore = useAuthStore()
        const callCenterStore = useCallCenterStore()
        this.socket.emit('user-activity', getDataToSendToSocket(authStore, callCenterStore, this.agentStatus))
      }
    },

    /**
     * Submits call wrap up data
     * @param call - Call data to wrap up
     */
    submitCallWrapUp(call: unknown): void {
      if (!this.socket) {
        return
      }

      this.socket.emit('submit-call-wrapup', call)
    },

    /**
     * Updates all users in the store
     * @param users - Array of users to update
     */
    updateAllUsers(users: SocketUser[]): void {
      this.usersPresence = users.map(user => computePresenceDisplay(user))
    },

    /**
     * Emits call center update to socket
     */
    emitCallCenterUpdate(): void {
      if (this.socket) {
        const authStore = useAuthStore()
        const callCenterStore = useCallCenterStore()
        this.socket.emit(
          'user-update-active-call-center',
          getDataToSendToSocket(authStore, callCenterStore, this.agentStatus)
        )
      }
    }
  }
})
